"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PeerDiscovery = exports.createDistantDhtAddress = void 0;
const utils_1 = require("@streamr/utils");
const identifiers_1 = require("../../identifiers");
const getClosestNodes_1 = require("../contact/getClosestNodes");
const ringIdentifiers_1 = require("../contact/ringIdentifiers");
const DiscoverySession_1 = require("./DiscoverySession");
const RingDiscoverySession_1 = require("./RingDiscoverySession");
const DhtNode_1 = require("../DhtNode");
const createDistantDhtAddress = (address) => {
    const raw = (0, identifiers_1.toDhtAddressRaw)(address);
    const flipped = raw.map((val) => ~val);
    return (0, identifiers_1.toDhtAddress)(flipped);
};
exports.createDistantDhtAddress = createDistantDhtAddress;
const logger = new utils_1.Logger(module);
class PeerDiscovery {
    ongoingDiscoverySessions = new Map();
    ongoingRingDiscoverySessions = new Map();
    rejoinOngoing = false;
    joinCalled = false;
    recoveryIntervalStarted = false;
    options;
    constructor(options) {
        this.options = options;
    }
    async joinDht(entryPoints, doAdditionalDistantPeerDiscovery = true, retry = true) {
        const contactedPeers = new Set();
        const distantJoinOptions = doAdditionalDistantPeerDiscovery
            ? { enabled: true, contactedPeers: new Set() } : { enabled: false };
        await Promise.all(entryPoints.map((entryPoint) => this.joinThroughEntryPoint(entryPoint, contactedPeers, distantJoinOptions, retry)));
    }
    async joinThroughEntryPoint(entryPointDescriptor, 
    // Note that this set is mutated by DiscoverySession
    contactedPeers, additionalDistantJoin, retry = true) {
        if (this.isStopped()) {
            return;
        }
        this.joinCalled = true;
        logger.debug(`Joining ${this.options.serviceId === DhtNode_1.CONTROL_LAYER_NODE_SERVICE_ID
            ? 'The Streamr Network' : `Control Layer for ${this.options.serviceId}`}`
            + ` via entrypoint ${(0, identifiers_1.toNodeId)(entryPointDescriptor)}`);
        if ((0, identifiers_1.areEqualPeerDescriptors)(entryPointDescriptor, this.options.localPeerDescriptor)) {
            return;
        }
        this.options.connectionLocker?.lockConnection(entryPointDescriptor, `${this.options.serviceId}::joinDht`);
        this.options.peerManager.addContact(entryPointDescriptor);
        const targetId = (0, identifiers_1.toNodeId)(this.options.localPeerDescriptor);
        const sessions = [this.createSession(targetId, contactedPeers)];
        if (additionalDistantJoin.enabled) {
            sessions.push(this.createSession((0, exports.createDistantDhtAddress)(targetId), additionalDistantJoin.contactedPeers));
        }
        await this.runSessions(sessions, entryPointDescriptor, retry);
        this.options.connectionLocker?.unlockConnection(entryPointDescriptor, `${this.options.serviceId}::joinDht`);
    }
    async joinRing() {
        const contactedPeers = new Set();
        const sessions = [this.createRingSession((0, ringIdentifiers_1.getRingIdRawFromPeerDescriptor)(this.options.localPeerDescriptor), contactedPeers)];
        await this.runRingSessions(sessions);
    }
    createSession(targetId, contactedPeers) {
        const sessionOptions = {
            targetId,
            parallelism: this.options.parallelism,
            noProgressLimit: this.options.joinNoProgressLimit,
            peerManager: this.options.peerManager,
            contactedPeers,
            abortSignal: this.options.abortSignal,
            createDhtNodeRpcRemote: this.options.createDhtNodeRpcRemote
        };
        return new DiscoverySession_1.DiscoverySession(sessionOptions);
    }
    createRingSession(targetId, contactedPeers) {
        const sessionOptions = {
            targetId,
            parallelism: this.options.parallelism,
            noProgressLimit: this.options.joinNoProgressLimit,
            peerManager: this.options.peerManager,
            contactedPeers,
            abortSignal: this.options.abortSignal,
            createDhtNodeRpcRemote: this.options.createDhtNodeRpcRemote
        };
        return new RingDiscoverySession_1.RingDiscoverySession(sessionOptions);
    }
    async runSessions(sessions, entryPointDescriptor, retry) {
        try {
            for (const session of sessions) {
                this.ongoingDiscoverySessions.set(session.id, session);
                await session.findClosestNodes(this.options.joinTimeout);
            }
        }
        catch (_e) {
            logger.debug(`DHT join on ${this.options.serviceId} timed out`);
        }
        finally {
            if (!this.isStopped()) {
                if (this.options.peerManager.getNeighborCount() === 0) {
                    if (retry) {
                        // TODO should we catch possible promise rejection?
                        // TODO use options option or named constant?
                        (0, utils_1.setAbortableTimeout)(() => this.rejoinDht(entryPointDescriptor), 1000, this.options.abortSignal);
                    }
                }
                else {
                    await this.ensureRecoveryIntervalIsRunning();
                }
            }
            sessions.forEach((session) => this.ongoingDiscoverySessions.delete(session.id));
        }
    }
    async runRingSessions(sessions) {
        try {
            for (const session of sessions) {
                this.ongoingRingDiscoverySessions.set(session.id, session);
                await session.findClosestNodes(this.options.joinTimeout);
            }
        }
        catch (_e) {
            logger.debug(`Ring join on ${this.options.serviceId} timed out`);
        }
        finally {
            sessions.forEach((session) => this.ongoingDiscoverySessions.delete(session.id));
        }
    }
    async rejoinDht(entryPoint, contactedPeers = new Set(), distantJoinContactPeers = new Set()) {
        if (this.isStopped() || this.rejoinOngoing) {
            return;
        }
        logger.debug(`Rejoining DHT ${this.options.serviceId}`);
        this.rejoinOngoing = true;
        try {
            await this.joinThroughEntryPoint(entryPoint, contactedPeers, { enabled: true, contactedPeers: distantJoinContactPeers });
            logger.debug(`Rejoined DHT successfully ${this.options.serviceId}!`);
        }
        catch {
            logger.warn(`Rejoining DHT ${this.options.serviceId} failed`);
            if (!this.isStopped()) {
                // TODO should we catch possible promise rejection?
                // TODO use options option or named constant?
                (0, utils_1.setAbortableTimeout)(() => this.rejoinDht(entryPoint), 5000, this.options.abortSignal);
            }
        }
        finally {
            this.rejoinOngoing = false;
        }
    }
    async ensureRecoveryIntervalIsRunning() {
        if (!this.recoveryIntervalStarted) {
            this.recoveryIntervalStarted = true;
            // TODO use options option or named constant?
            await (0, utils_1.scheduleAtInterval)(() => this.fetchClosestAndRandomNeighbors(), 60000, true, this.options.abortSignal);
        }
    }
    async fetchClosestAndRandomNeighbors() {
        if (this.isStopped()) {
            return;
        }
        const localNodeId = (0, identifiers_1.toNodeId)(this.options.localPeerDescriptor);
        const nodes = this.getClosestNeighbors(localNodeId, this.options.parallelism);
        const randomNodes = this.getClosestNeighbors((0, identifiers_1.randomDhtAddress)(), 1);
        await Promise.allSettled([
            ...nodes.map(async (node) => {
                const remote = this.options.createDhtNodeRpcRemote(node);
                const contacts = await remote.getClosestPeers(localNodeId);
                for (const contact of contacts) {
                    this.options.peerManager.addContact(contact);
                }
            }),
            ...randomNodes.map(async (node) => {
                const remote = this.options.createDhtNodeRpcRemote(node);
                const contacts = await remote.getClosestPeers((0, identifiers_1.randomDhtAddress)());
                for (const contact of contacts) {
                    this.options.peerManager.addContact(contact);
                }
            })
        ]);
    }
    getClosestNeighbors(referenceId, maxCount) {
        return (0, getClosestNodes_1.getClosestNodes)(referenceId, this.options.peerManager.getNeighbors().map((n) => n.getPeerDescriptor()), { maxCount });
    }
    isJoinOngoing() {
        return !this.joinCalled ? true : this.ongoingDiscoverySessions.size > 0;
    }
    isJoinCalled() {
        return this.joinCalled;
    }
    isStopped() {
        return this.options.abortSignal.aborted;
    }
}
exports.PeerDiscovery = PeerDiscovery;
//# sourceMappingURL=PeerDiscovery.js.map